/**
 * TenantResolverService — resolves tenantId from hostname at boot.
 *
 * CRUISE-TENANT-1: Runs synchronously before React mounts. // foundation-purity-allow: session-id label only
 * The resolved value is memoized in _tenantId and mirrored to
 * window.__KAIROS_TENANT_ID__ by main.jsx before ReactDOM.render.
 *
 * Resolution priority:
 *   1. ?tenant=<id> query param  (dev/testing override)
 *   2. Subdomain mapping from window.location.hostname
 *   3. Fallback: "elyon-demo"
 *
 * Subdomain map:
 *   app.ncl.elyonsci.ai       → "ncl"
 *   app.riverside.elyonsci.ai → "riverside"
 *   app.elyonsci.ai           → "elyon-demo"
 *   localhost / 127.0.0.1     → VITE_DEV_TENANT_ID || "elyon-demo"
 */

import { DEMO_TENANT_ID } from '../data/tenantDefaults.js';

let _tenantId = null;

/**
 * Pure resolver — maps a hostname string to a tenantId.
 * Exported for unit testing (G1–G3 acceptance gates).
 *
 * @param {string} hostname
 * @returns {string}
 */
export function resolveTenanIdFromHostname(hostname) {
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return (typeof import.meta !== 'undefined' && import.meta.env?.VITE_DEV_TENANT_ID) || DEMO_TENANT_ID;
  }

  // app.{tenant}.elyonsci.ai  →  4-part hostname, parts[0]="app", parts[2]="elyonsci"
  const parts = hostname.split('.');
  if (
    parts.length >= 4 &&
    parts[0] === 'app' &&
    parts[parts.length - 2] === 'elyonsci' &&
    parts[parts.length - 1] === 'ai'
  ) {
    return parts[1];
  }

  // app.elyonsci.ai  →  3-part, ratified canonical tenant (TENANT-RESOLVER-UNIFY-1)
  if (hostname === 'app.elyonsci.ai') {
    return 'elyon-demo';
  }

  return DEMO_TENANT_ID;
}

/**
 * Memoized resolver — called once at boot (main.jsx), subsequent calls
 * return the cached value.
 *
 * @returns {string} tenantId
 */
export function getCurrentTenantId() {
  if (_tenantId !== null) return _tenantId;

  // 1. Query param override (?tenant=ncl)
  try {
    const param = new URLSearchParams(window.location.search).get('tenant');
    if (param && param.trim()) {
      _tenantId = param.trim();
      return _tenantId;
    }
  } catch (_) {
    // non-browser environment (tests) — fall through
  }

  // 2. Hostname-based resolution
  try {
    _tenantId = resolveTenanIdFromHostname(window.location.hostname);
  } catch (_) {
    _tenantId = DEMO_TENANT_ID;
  }

  return _tenantId;
}

/** Reset memo — test-only, never call in production code. */
export function _resetTenantIdForTests() {
  _tenantId = null;
}
